import { HexColorPicker } from "react-colorful";
import { useEffect, useRef, useState } from "react";
import { Button } from "./Button";
import { Text } from "./Text";

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const ColorInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
	const [draft, setDraft] = useState(value);

	const changeDraft = (next: string) => {
		setDraft(next);
		if (HEX_COLOR.test(next)) onChange(next);
	};

	return (
		<input
			value={draft}
			inputMode="text"
			maxLength={7}
			className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1.5 font-mono text-sm outline-none focus:border-accent"
			onChange={(event) => changeDraft(event.target.value)}
			onBlur={() => setDraft(value)}
		/>
	);
};

/**
 * Поле произвольного цвета с одинаковым picker на desktop и touch-устройствах.
 * `up` — палитра открывается вверх: для полей у нижнего края экрана.
 */
export const ColorPicker = ({
	label,
	value,
	onChange,
	up = false,
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
	up?: boolean;
}) => {
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		const closeOnPointerDown = (event: PointerEvent) => {
			if (!root.current?.contains(event.target as Node)) setOpen(false);
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		document.addEventListener("pointerdown", closeOnPointerDown);
		document.addEventListener("keydown", closeOnEscape);
		return () => {
			document.removeEventListener("pointerdown", closeOnPointerDown);
			document.removeEventListener("keydown", closeOnEscape);
		};
	}, [open]);

	return (
		<div
			ref={root}
			className="relative flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold text-ink"
		>
			<Text as="span">{label}</Text>
			<Button
				type="button"
				border={false}
				aria-label={label || "Выбрать цвет"}
				aria-expanded={open}
				className="size-7 rounded-sm border border-border p-0 shadow-inner"
				style={{ backgroundColor: value }}
				onClick={() => setOpen((current) => !current)}
			/>
			{open && (
				<div
					className={`absolute right-0 z-100 w-56 ${up ? "bottom-full mb-2" : "top-full mt-2"} rounded-lg border border-border bg-surface p-3 shadow-xl`}
				>
					<HexColorPicker color={value} onChange={onChange} className="w-full" />
					<label className="mt-3 flex items-center gap-2 text-xs text-ink">
						<Text as="span">HEX</Text>
						<ColorInput key={value} value={value} onChange={onChange} />
					</label>
				</div>
			)}
		</div>
	);
};
